import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { SvgIcon, Range, Button, Typography, useRerender } from "@gemlightbox/core-kit";

import { useStores } from "src/hooks";
import { editMediaStore } from "../../../edit-media.store";
import { GroupBlock } from "../../group-block";

import { ReactComponent as RetouchIcon } from "src/external-ts/assets/images/edit-media/dust-remove-icon.svg";
import { ReactComponent as EraserIcon } from "src/external-ts/assets/images/edit-media/eraser.svg";
import styles from "./brush-tools.module.css";

const DEFAULT_BRUSH_SIZE = 40;
const DEFAULT_STRENGTH_PCT = 100;

type BrushType = "retouch" | "erase";

export const BrushTools: React.FC = observer(() => {
  const rerender = useRerender();
  const { localeStore } = useStores();
  const { renderer, mainImageComponent } = editMediaStore;
  const { retouchTool, eraseTool, handSelectionTool } = renderer.tools;

  const isRetouchToolActive = !!retouchTool && renderer.eventsManager.checkForTool(retouchTool);
  const isEraseToolActive = !!eraseTool && renderer.eventsManager.checkForTool(eraseTool);

  const lastToolRef = useRef<BrushType>("retouch");

  useEffect(() => {
    if (retouchTool) {
      if (!retouchTool.brushSize || retouchTool.brushSize === 1) {
        retouchTool.brushSize = DEFAULT_BRUSH_SIZE;
      }
      if (typeof retouchTool.strength !== "number") {
        retouchTool.strength = DEFAULT_STRENGTH_PCT / 100;
      }
    }

    if (eraseTool) {
      if (!eraseTool.brushSize || eraseTool.brushSize === 1) {
        eraseTool.brushSize = DEFAULT_BRUSH_SIZE;
      }
    }

    rerender();
  }, [eraseTool, retouchTool, rerender]);

  const brushType: BrushType = isRetouchToolActive
    ? "retouch"
    : isEraseToolActive
    ? "erase"
    : lastToolRef.current;

  const activeTool = brushType === "retouch" ? retouchTool : eraseTool;

  const handleSelectTool = (type: BrushType) => () => {
    lastToolRef.current = type;

    const tool = type === "retouch" ? retouchTool : eraseTool;
    if (!tool) return;

    const isActive = renderer.eventsManager.checkForTool(tool);

    !renderer.hasSelectedComponent && renderer.selectComponent(mainImageComponent);
    renderer.eventsManager.assignTool(isActive ? handSelectionTool : tool);
    rerender();
  };

  const handleBrushSize = (value: number) => {
    if (!activeTool) return;
    activeTool.brushSize = value;
    rerender();
  };

  const handleStrength = (value: number) => {
    if (!retouchTool) return;
    retouchTool.strength = value / 100;
    rerender();
  };

  return (
    <GroupBlock className={styles.brushBlockGroup}>
      <Typography size="small600" color="textSecondary">
        {localeStore.t('["edit-media"].inspector.adjustments.eraser.title')}
      </Typography>

      <div className={styles.buttons}>
        <Button
          appearance={isRetouchToolActive ? "primaryOutlined" : "tertiaryOutlined"}
          onClick={handleSelectTool("retouch")}
          data-cy="retouch-brush-button"
        >
          <SvgIcon icon={RetouchIcon} />
          {localeStore.t('["edit-media"].inspector.adjustments["retouch-brush"]["retouch-btn"]')}
        </Button>

        <Button
          appearance={isEraseToolActive ? "primaryOutlined" : "tertiaryOutlined"}
          onClick={handleSelectTool("erase")}
          data-cy="eraser-button"
        >
          <SvgIcon icon={EraserIcon} />
          {localeStore.t('["edit-media"].inspector.adjustments.eraser["erase-btn"]')}
        </Button>
      </div>

      <Range
        label={localeStore.t('["edit-media"].inspector.adjustments.eraser.range')}
        min={1}
        max={1000}
        step={1}
        value={activeTool?.brushSize ?? DEFAULT_BRUSH_SIZE}
        onChange={handleBrushSize}
        disableError
        showRangeValue
      />

      {/* {brushType === "retouch" && (
        <Range
          label={localeStore.t('["edit-media"].inspector.adjustments["retouch-brush"].strength')}
          min={0}
          max={100}
          step={1}
          value={Math.round((retouchTool?.strength ?? DEFAULT_STRENGTH_PCT / 100) * 100)}
          onChange={handleStrength}
          disableError
          showRangeValue
        />
      )} */}
    </GroupBlock>
  );
});

export default BrushTools;
